import React, { useMemo } from 'react'

import Image from '../../../../../components/pdf/Image'
import Separator from '../../../../../components/pdf/Separator'
import Text from '../../../../../components/pdf/Text'
import View from '../../../../../components/pdf/View'
import { getDestinoDaAtividade, formatDateBr, getDataElaboracao, getVigenciaAte } from '../../_helpers/pdfHelpers'

/**
 * Capa. Redesenhada para reproduzir o layout real da capa do modelo aprovado (confirmado
 * por inspeção pixel-a-pixel do PDF-modelo, não presumido):
 * - Logo Bradesco alinhado à esquerda (não centralizado, como estava antes).
 * - Título único, em preto, centralizado: "PROGRAMA DE GERENCIAMENTO DE RISCOS GRO/PGR"
 *   — o modelo real não traz nem a linha "PGR" separada em cima, nem o subtítulo
 *   "(Em atendimento a NR-1...)"; ambos eram uma suposição de capa institucional
 *   genérica. A referência à NR-1 já existe no corpo do documento (Seção 3 - Objetivo).
 * - Linha de Destino da Atividade como texto simples (não célula de tabela).
 * - "Data de Elaboração:" perto da base da página, como texto simples — SEM tabela (o
 *   modelo real não usa tabela na capa; a tabela anterior era o padrão genérico do Btime).
 * - Rodapé "Segurança do Trabalho – V0_26" já vem do `footer` do `<Page>` (ver
 *   `PdfDocument.js` / `sections/Footer.js`), não é repetido aqui.
 *
 * TIPOGRAFIA DO TÍTULO/DESTINO (ajuste pontual pedido pela Julia, ver "Ajustes bradesco.pdf"):
 * título principal centralizado, Arial, 28pt, negrito; linha de Destino da Atividade abaixo
 * dele centralizada, Arial, 20pt, SEM negrito, com espaçamento vertical deliberadamente maior
 * entre as duas (`Separator weight={45}`) do que o espaçamento padrão do documento. O
 * `style` explícito (`fontFamily`/`fontSize` em pt, não no `size`/`fontBase` genérico de
 * `components/pdf/Text.js`) é necessário porque esses dois valores (28pt/20pt) são fixos e
 * não escalam com a opção de "tamanho de fonte base" do formulário de opções do PDF — nem
 * correspondem a nenhum dos tamanhos nomeados (`h1`..`h6`, `p`...) já definidos em
 * `utils/constants/fontSizes.js`. Arial já é forçada em toda a árvore `#pdfPage` pelo
 * `<style>` global de `sections/Header.js` (`#pdfPage, #pdfPage * { font-family: Arial... }
 * !important`), mas é declarada aqui também de forma explícita e autocontida, sem depender
 * dessa regra global de outro componente.
 *
 * "Vigência até" é mantida como uma segunda linha logo abaixo de "Data de Elaboração" (o
 * modelo real também traz esse dado na capa). Ver `getVigenciaAte` em
 * `../../_helpers/pdfHelpers.js` para a regra de cálculo (data de elaboração + 2 anos,
 * ajustada para o último dia do mês resultante — regra nova, substitui o antigo "+730
 * dias").
 *
 * Linha abaixo do título = "Destino da Atividade", isto é, o Local selecionado no campo
 * "Destino" da criação da OS (`serviceOrder.place`, ver `getDestinoDaAtividade` em
 * `../../_helpers/pdfHelpers.js`) — CORRIGIDO nesta revisão (bug reportado por Julia Buzzo,
 * PM, 2026-08-30): a revisão anterior compunha esta linha como Junção + EMPRESA + nome do
 * Local (`{juncao} - {serviceOrder.company?.name} - {serviceOrder.place?.name}`), o que é
 * um entendimento equivocado do dado — nem "Junção" (nível 4 de
 * `place.placeLevelsResume`, ver `01.InformacoesIniciaisSection.js`) nem a EMPRESA do
 * cliente (`serviceOrder.company`) fazem parte do conceito de "Destino"; misturar os três
 * é exatamente o que produzia a capa quebrada "N/A - - 2160" (Junção sem nível 4
 * cadastrado -> fallback "N/A" de `getPlaceLevelNameByIndexOrFallback`, Empresa não
 * cadastrada nesta OS -> vazio, e "2160" era o único pedaço correto — `place.name` — só
 * que exibido sozinho, sem o `place.resume`, e cercado pelos dois campos errados).
 *
 * ORDEM corrigida em 2026-09-01 (2ª rodada de feedback da Julia): o formato usado após o
 * PR #739 (`place.resume + ' - ' + place.name`, por analogia ao rótulo "Destino" de outros
 * PDFs — `serviceOrders/pdf/general/Overview.js`, `pdfs/lmdasilva/general/Overview.js`,
 * `pdfs/meta/general/Overview.js`) produzia a ordem INVERTIDA nesta capa ("SILVA
 * JARDIM-UCTBA - 2160" em vez de "2160 - SILVA JARDIM-UCTBA"), porque `place.name` aqui é
 * o código da agência e `place.resume` é o nome descritivo — o inverso do que esse padrão
 * genérico assume implicitamente. Corrigido para `place.name + ' - ' + place.resume` em
 * `getDestinoDaAtividade` (ver esse helper para o detalhe completo).
 *
 * "Vigência até" é mantida como uma segunda linha logo abaixo de "Data de Elaboração":
 * no modelo real esse dado aparece na página seguinte à capa (junto de um Sumário/Índice
 * com numeração de página) — um Sumário dinâmico não é viável nesta arquitetura de
 * impressão via navegador (exigiria contagem de página por referência, sem suporte
 * confiável) e está fora do escopo desta correção visual; ver observação no PR. Mantendo
 * o dado (já calculado, não é lógica nova) visível na capa evita simplesmente escondê-lo.
 *
 * A marca d'água "EXEMPLO CONFIDENCIAL - NÃO REPRODUZIR" / "MARCA DAGUA - NÃO OFICIAL"
 * presente no arquivo de modelo fornecido por Julia é exclusiva do exemplo enviado —
 * NÃO é reproduzida aqui, por não fazer parte do documento final gerado pelo sistema.
 */
const TITLE_FONT_FAMILY = 'Arial, Helvetica, sans-serif'

const CoverSection = ({ serviceOrder }) => {
  const dataElaboracao = useMemo(() => getDataElaboracao(serviceOrder), [serviceOrder])
  const vigenciaAte = useMemo(() => getVigenciaAte(dataElaboracao), [dataElaboracao])

  const destino = getDestinoDaAtividade(serviceOrder.place)

  return (
    <View pageBreakAfter>
      <Image src="/pdfs/bradesco/bradesco-logo.png" maxWidth={130} ignoreFixedSize />

      <Separator weight={70} />

      <Text bold align="center" color="#000000" style={{ fontFamily: TITLE_FONT_FAMILY, fontSize: '28pt' }}>
        PROGRAMA DE GERENCIAMENTO DE RISCOS GRO/PGR
      </Text>

      {/* Espaçamento deliberado entre o título e a linha de Destino da Atividade abaixo
          (pedido explícito da Julia — ver PR), maior que o espaçamento "de parágrafo"
          padrão (Separator weight={15}) usado no resto do documento. */}
      <Separator weight={45} />

      <Text align="center" color="#000000" style={{ fontFamily: TITLE_FONT_FAMILY, fontSize: '20pt', fontWeight: 400 }}>
        {destino}
      </Text>

      <Separator weight={50} />

      <Text size="p" color="#000000">
        <Text bold inline color="#000000">
          Data de Elaboração:
        </Text>{' '}
        {formatDateBr(dataElaboracao)}
      </Text>
      <Separator weight={8} />

      <Text size="p" color="#000000">
        <Text bold inline color="#000000">
          Vigência até:
        </Text>{' '}
        {formatDateBr(vigenciaAte)}
      </Text>
    </View>
  )
}

export default CoverSection
